import { Box, Button, Card, CardContent, Chip, Stack, Typography } from '@mui/material';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import EventRounded from '@mui/icons-material/EventRounded';
import HelpOutlineRounded from '@mui/icons-material/HelpOutlineRounded';
import TimerOutlined from '@mui/icons-material/TimerOutlined';
import { Link as RouterLink } from 'react-router';
import { formatDateTime, formatPercent, formatRelative } from '@/utils/format';
import { MY_STATE } from '../meta';
import type { MyQuiz } from '../types';
import { KindLabel } from './bits';

function Meta({ icon: Icon, children }: { icon: typeof EventRounded; children: React.ReactNode }) {
  return (
    <Stack
      direction="row"
      spacing={0.75}
      sx={{ alignItems: 'center', color: 'text.secondary', minWidth: 0 }}
    >
      <Icon sx={{ fontSize: 18 }} />
      <Typography variant="body2" noWrap>
        {children}
      </Typography>
    </Stack>
  );
}

/** A student's view of one assigned quiz, with the right call to action for its state. */
export function QuizCard({ quiz }: { quiz: MyQuiz }) {
  const state = MY_STATE[quiz.state];
  const last = quiz.lastAttempt;
  const cta =
    quiz.state === 'in_progress'
      ? { label: 'Continuar', to: `/exam/${quiz.currentAttemptId}`, variant: 'contained' as const }
      : quiz.state === 'available'
        ? {
            label: quiz.attemptsUsed > 0 ? 'Tentar de novo' : 'Começar',
            to: `/assessments/my/${quiz.id}`,
            variant: 'contained' as const,
          }
        : last
          ? {
              label: 'Ver resultado',
              to: `/assessments/attempts/${last.id}/result`,
              variant: 'tonal' as const,
            }
          : { label: 'Detalhes', to: `/assessments/my/${quiz.id}`, variant: 'tonal' as const };

  return (
    <Card
      sx={(theme) => ({
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        ...(quiz.state === 'in_progress' && { borderColor: theme.vars.palette.info.main }),
        '&:hover': { borderColor: 'primary.light' },
      })}
    >
      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Stack
          direction="row"
          spacing={1}
          sx={{ justifyContent: 'space-between', alignItems: 'flex-start' }}
        >
          <Box sx={{ minWidth: 0 }}>
            <KindLabel kind={quiz.kind} />
            <Typography variant="h6" component="h3" sx={{ overflowWrap: 'anywhere' }}>
              {quiz.title}
            </Typography>
          </Box>
          <Chip
            size="small"
            variant="soft"
            color={state.color}
            label={state.label}
            sx={{ flexShrink: 0 }}
          />
        </Stack>
        {quiz.description && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {quiz.description}
          </Typography>
        )}
        <Stack direction="row" spacing={2} useFlexGap sx={{ flexWrap: 'wrap' }}>
          <Meta icon={HelpOutlineRounded}>{quiz.questionCount} questões</Meta>
          {quiz.timeLimitMinutes && <Meta icon={TimerOutlined}>{quiz.timeLimitMinutes} min</Meta>}
          {quiz.state === 'upcoming' && quiz.availableFrom ? (
            <Meta icon={EventRounded}>Abre {formatRelative(quiz.availableFrom)}</Meta>
          ) : quiz.availableUntil ? (
            <Meta icon={EventRounded}>
              {quiz.state === 'available' || quiz.state === 'in_progress'
                ? `Até ${formatDateTime(quiz.availableUntil)}`
                : `Encerrou ${formatRelative(quiz.availableUntil)}`}
            </Meta>
          ) : null}
        </Stack>
        <Box sx={{ flex: 1 }} />
        <Stack
          direction="row"
          sx={{ alignItems: 'center', justifyContent: 'space-between', gap: 1 }}
        >
          <Typography variant="body2" color="text.secondary">
            {last
              ? last.scoreVisible
                ? `Sua nota: ${formatPercent(last.percent)}`
                : last.status === 'needs_review'
                  ? 'Aguardando correção'
                  : 'Nota liberada em breve'
              : quiz.maxAttempts > 1
                ? `${quiz.maxAttempts} tentativas`
                : quiz.maxAttempts === 0
                  ? 'Tentativas ilimitadas'
                  : 'Tentativa única'}
          </Typography>
          {quiz.state !== 'upcoming' && quiz.state !== 'missed' && (
            <Button
              variant={cta.variant}
              component={RouterLink}
              to={cta.to}
              endIcon={<ArrowForwardRounded />}
            >
              {cta.label}
            </Button>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}
